import { defineRouting } from 'next-intl/routing';

import { defaultLocale } from './default-locale';
import { locales } from './locales';

/**
 * How a request's locale is decided.
 *
 * ## Why the locale is not in the URL
 *
 * `localePrefix: 'never'` — an Arabic operator reads the console at `/home/...`
 * exactly as an English one does, and next-intl rewrites internally to the
 * `[locale]` segment.
 *
 * The alternative, `'as-needed'`, would put Arabic at `/ar/home/...`. That
 * reads better in a URL bar and is wrong here: this app links with plain
 * `next/link` and raw `pathsConfig` strings throughout, none of which carry a
 * prefix. The first sidebar click would drop `/ar` and silently return the
 * operator to English. Prefixing would mean converting every link in the app to
 * next-intl's `Link` — a large change whose only benefit is a nicer URL.
 *
 * ## Why detection stays on
 *
 * The locale is a stored user preference, and `localeCookie` (`NEXT_LOCALE`) is
 * how it reaches the middleware. Setting `localeDetection: false` would have
 * been the obvious way to stop the browser guessing — but it disables the
 * cookie too, not just the `accept-language` header, leaving no way to honour
 * an explicit choice. See `resolveLocaleFromPrefix` in next-intl.
 *
 * So detection stays on for the cookie, and `proxy.ts` drops the
 * `accept-language` header when no cookie is set. The effect is that the only
 * way to get a non-default locale is to have asked for one: an Arabic browser
 * arriving at the marketing site gets English, which is the honest answer while
 * the marketing pages are English-only.
 */
/**
 * Where the chosen locale is carried between requests.
 *
 * Named explicitly rather than relying on next-intl's default so the proxy and
 * the language switcher can import the same constant and cannot drift from what
 * the middleware actually reads.
 */
export const LOCALE_COOKIE = 'NEXT_LOCALE';

export const routing = defineRouting({
  locales,
  defaultLocale,
  localePrefix: 'never',
  localeDetection: true,
  localeCookie: {
    name: LOCALE_COOKIE,
    // A preference, not a session. It should survive a browser restart, and it
    // carries nothing an attacker would want — so no `httpOnly`, which lets the
    // switcher set it client-side on the sign-in page, before there is a user
    // whose stored preference could be read.
    maxAge: 60 * 60 * 24 * 365,
    sameSite: 'lax',
  },
});

// Export locale types for TypeScript
export type Locale = (typeof routing.locales)[number];
